import cx from 'classnames'
import { CSSProperties } from 'react'
import { GroupBase, StylesConfig } from 'react-select'

import { colors } from './theme'

export interface StyleOverrides {
  control?: CSSProperties
  valueContainer?: CSSProperties
  singleValue?: CSSProperties
  placeholder?: CSSProperties
  input?: CSSProperties
  menu?: CSSProperties
  indicatorSeparator?: CSSProperties
}

export interface SearchDropdownOption {
  label: string
  value: string
}

export interface AutocompleteOption {
  value: string
  title: string
}

/**
 * Gets nonce value.
 */
export const getNonce = () => {
  return (
    document.querySelector('[property="csp-nonce"]')?.getAttribute('content') ??
    ''
  )
}

/**
 * Determines if stylesheet URL is added to document.
 */
const stylesheetExists = (url: string): boolean => {
  return [...document.styleSheets].some(({ href }) => {
    return href && href.indexOf(url) > -1
  })
}

/**
 * Adds stylesheets to document.
 */
export const appendStylesheet = (url: string, nonce: string) => {
  // If stylesheet already exists, stop
  if (stylesheetExists(url)) {
    return
  }

  const link = document.createElement('link')
  link.href = url
  link.rel = 'stylesheet'
  link.type = 'text/css'
  link.setAttribute('nonce', nonce)

  document.head.appendChild(link)
}

/**
 * Gets CSS class that sets grid size.
 */
export const getGridSizeClass = (
  breakpoint?: string,
  size?: number,
  justify: string | boolean = false,
  align: string | boolean = false,
  start: number | boolean = false,
) => {
  const hasBreakpoint = breakpoint && breakpoint.trim()
  const colSpan = hasBreakpoint
    ? `${breakpoint}:col-span-${size}`
    : `col-span-${size}`
  const colStart = hasBreakpoint
    ? `${breakpoint}:col-start-${start}`
    : `col-start-${start}`
  const colJustify = hasBreakpoint ? `${breakpoint}:${justify}` : justify
  const colAlign = hasBreakpoint ? `${breakpoint}:${align}` : align

  return cx(
    colSpan,
    start && colStart,
    justify && colJustify,
    align && colAlign,
  )
}

/**
 * Gets CSS class that sets grid item order.
 */
export const getItemOrderClass = (
  index: number,
  breakpoint?: string,
  size?: number,
  totalSize?: number,
  reverseWhenFullWidth?: boolean,
) => {
  const hasBreakpoint = breakpoint && breakpoint.trim()
  const orderIndex =
    reverseWhenFullWidth && size === totalSize ? 12 - index : index + 1
  const order = hasBreakpoint
    ? `${breakpoint}:order-${orderIndex}`
    : `order-${orderIndex}`

  return cx(order)
}

export const getInputSearchDropdownStyles = (
  hasError: boolean,
  hasBorder: boolean,
  styleOverrides?: StyleOverrides,
) => {
  const inputSearchDropdownStyles: StylesConfig<
    SearchDropdownOption,
    false,
    GroupBase<SearchDropdownOption>
  > = {
    control: () => ({
      display: 'flex',
      borderRadius: 0,
      background: 'transparent',
      color: colors.current,
      padding: '.5rem 1rem',
      ...(hasBorder && {
        border: hasError
          ? `1px solid ${colors.error.DEFAULT}`
          : `1px solid currentColor`,
      }),
      ...(!hasBorder && {
        paddingLeft: 0,
        paddingRight: 0,
      }),

      ...styleOverrides?.control,
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: 0,
      color: colors.current,

      ...(!hasBorder && {
        paddingRight: '.5rem',
      }),

      ...styleOverrides?.valueContainer,
    }),
    singleValue: (provided) => ({
      ...provided,
      color: colors.current,
      textOverflow: 'ellipsis',

      ...styleOverrides?.singleValue,
    }),
    placeholder: (provided) => ({
      ...provided,
      margin: 0,
      color: colors.current,
      opacity: 0.5,

      ...styleOverrides?.placeholder,
    }),
    input: (provided) => ({
      ...provided,
      margin: '0 1rem 0 0',
      color: colors.current,
      padding: 0,

      ...styleOverrides?.input,
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: 0,
      minWidth: 200,
      color: colors.black,
      background: colors.white,

      ...styleOverrides?.menu,
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      display: 'none',

      ...styleOverrides?.indicatorSeparator,
    }),
  }

  return inputSearchDropdownStyles
}
